import Button from '@carvana/ds-button';
import { CarvanaEmblem, CarvanaHorizontal, ChatLogoCvna } from '@carvana/ds-icons';
import LinkButton from '|/components/carvana-ds/link-button';
import { ChevronLeft } from 'lucide-react';
import React, { useState } from 'react';
import { ExchangeBanner, ExchangeModal } from '|/app/header/exchange-banner';
import ImpersonationBanner from '|/app/header/impersonation-baner';
import LockTimer, { LockTimerModal } from '|/app/header/purchase-header/components/lock-timer';
import SlateHeader from '|/app/header/slate-header';
import { Summary } from '|/app/services';
import { SupportMfeModal } from '|/components/support-contact';
import { useSlate } from '|/context';
import { isCarvanaNativeApp, canHandleBackNavigation } from '|/helpers/is-native-app';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context/use-analytics-context';
import styles from './checkout-header.module.css';

interface CheckoutHeaderProps {
  orderSummary?: Summary | null;
}

const CheckoutHeader: React.FC<CheckoutHeaderProps> = ({ orderSummary = null }) => {
  const isMobileNative = isCarvanaNativeApp();
  const { isSlateVehicle } = useSlate();
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLockTimerModalOpen, setIsLockTimerModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();

  // Checkout shows the order summary inline (vehicle card), so the Slate header omits the chip.
  if (isSlateVehicle) {
    return <SlateHeader inlineBrand={true} />;
  }

  const onLogoClick = () => {
    trackEvent('pp_clicked_logo_icon');
    window.location.href = '/';
  };

  const onLockTimerClick = () => {
    trackEvent('pp_clicked_lock_timer');
    setIsLockTimerModalOpen(true);
  };

  const handleSupportClick = () => {
    trackEvent('pp_header_support_contact_clicked');
    setIsSupportModalOpen(true);
  };

  const saveAndExit = () => {
    sendNativeEvent({ event: 'save_exit', payload: {}, source: 'purchase' });
  };

  const handleBackButtonClick = () => {
    if (canHandleBackNavigation()) {
      sendNativeEvent({ event: 'webview_back_tapped', payload: {}, source: 'purchase' });
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      saveAndExit();
    }
  };

  return (
    <>
      <ImpersonationBanner />
      <header className={styles.header}>
        {isMobileNative && (
          <div className={styles.nativeHeader}>
            <Button
              variant="tertiary"
              size="xs"
              icon={true}
              data-analytics-id="pp_header_back_button"
              className={styles.backButton}
              onClick={handleBackButtonClick}
            >
              <ChevronLeft color="var(--icon-inverse)" />
            </Button>
            <LinkButton
              variant="inverse"
              size="sm"
              underline={false}
              className={styles.saveExitButton}
              data-analytics-id="pp_header_save_exit_button"
              onClick={saveAndExit}
            >
              Save & exit
            </LinkButton>
          </div>
        )}

        <div className={styles.headerContent}>
          {/* Mobile: emblem + divider + "Checkout" */}
          <div className={styles.mobileLeft}>
            {isMobileNative ? (
              <span aria-label="Carvana" className={styles.emblemLink}>
                <CarvanaEmblem className={styles.emblem} />
              </span>
            ) : (
              <a
                href="/"
                onClick={e => {
                  e.preventDefault();
                  onLogoClick();
                }}
                data-analytics-id="pp_header_home"
                aria-label="Carvana Home"
                className={styles.emblemLink}
              >
                <CarvanaEmblem className={styles.emblem} />
              </a>
            )}
            <span className={styles.mobileLeftDivider} aria-hidden="true" />
            <span className={styles.checkoutTitle}>Checkout</span>
          </div>

          {/* Desktop: full Carvana logo */}
          <a
            href="/"
            className={styles.desktopLogo}
            onClick={e => {
              e.preventDefault();
              onLogoClick();
            }}
            data-analytics-id="pp_header_home"
          >
            <CarvanaHorizontal className={styles.carvanaLogo} />
          </a>

          {/* Right content */}
          <div className={styles.rightContent}>
            <button
              className={styles.supportLink}
              onClick={handleSupportClick}
              data-analytics-id="pp_header_support_contact"
              type="button"
            >
              <ChatLogoCvna size={16} color="var(--icon-primary)" />
              <span>Support &amp; Contact</span>
            </button>

            <div className={styles.timerPlain}>
              <LockTimer
                orderSummary={orderSummary}
                onClick={onLockTimerClick}
                data-analytics-id="pp_header_lock_timer"
              />
            </div>
          </div>
        </div>
      </header>

      <ExchangeBanner
        orderSummary={orderSummary}
        onSeeDetails={() => {
          setIsExchangeModalOpen(true);
        }}
      />

      <ExchangeModal
        isOpen={isExchangeModalOpen}
        orderSummary={orderSummary}
        onClose={() => {
          setIsExchangeModalOpen(false);
        }}
      />

      <LockTimerModal
        isOpen={isLockTimerModalOpen}
        onClose={() => {
          setIsLockTimerModalOpen(false);
        }}
      />

      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
      />
    </>
  );
};

export default CheckoutHeader;
